// Stripe's card fields render in Stripe's iframe, out of reach of our CSS; this hands the theme's
// colours (the --sf-* variables StorefrontThemeHelper writes) to Stripe's appearance API.
// Returns {} on stores without a theme, so Stripe keeps its default look.
export const storefrontStripeAppearance = (): any => {
  const css = getComputedStyle(document.documentElement);
  const value = (name: string) => css.getPropertyValue(`--sf-${name}`).trim();
  if (!value("ink")) return {};

  const variables: any = {
    colorText: value("ink"),
    colorTextSecondary: value("muted"),
    colorTextPlaceholder: value("muted"),
    colorBackground: value("surface"),
    colorPrimary: value("accent"),
    colorDanger: value("error"),
    borderRadius: value("radius"),
  };
  Object.keys(variables).forEach((key) => !variables[key] && delete variables[key]);

  return {
    variables,
    rules: {
      ".Input": { borderColor: value("input-line") || value("line") },
    },
  };
};

// The same for the separate card number / expiry / CVC fields (Stripe's card elements take
// their colours as a style option)
export const storefrontStripeCardOptions = (): any => {
  const css = getComputedStyle(document.documentElement);
  const value = (name: string) => css.getPropertyValue(`--sf-${name}`).trim();
  if (!value("ink")) return {};

  return {
    style: {
      base: {
        color: value("ink"),
        iconColor: value("muted"),
        "::placeholder": { color: value("muted") },
      },
      invalid: {
        color: value("error") || undefined,
        iconColor: value("error") || undefined,
      },
    },
  };
};

// The theme's template and template options, written by StorefrontThemeHelper on #root
// (data-menu-template, data-menu-options). Empty without a theme, so every option keeps
// today's behaviour: search shown, Add to Order button, sold-out items greyed out.
let menuOptions: any = null;
export const storefrontMenuOptions = (): any => {
  if (menuOptions) return menuOptions;
  const root = document.getElementById("root");
  try {
    menuOptions = JSON.parse(root?.dataset?.menuOptions || "{}") || {};
  } catch (e) {
    menuOptions = {};
  }
  return menuOptions;
};

// Server-rendered on #root and fixed for the page's life, so read once like the options
let menuTemplate: string | undefined;
export const storefrontMenuTemplate = (): string => {
  if (menuTemplate === undefined) {
    menuTemplate = document.getElementById("root")?.dataset?.menuTemplate || "";
  }
  return menuTemplate;
};

// Themed stores carry a server-rendered sf-theme-* body class, fixed for the page's life
export const isStorefrontThemed = (): boolean => /\bsf-theme-/.test(document.body.className);

// Theme option sold_out: hide. Sold-out items are left out, and a category (or grouped item) whose
// items are all hidden is not shown, in the menu or the category nav. Unchanged otherwise.
export const hideSoldOutItems = (categories: any[], outOfStockItems: any[]): any[] => {
  if (storefrontMenuOptions().sold_out !== "hide" || !outOfStockItems?.length) return categories;
  const inStock = (item: any) => !outOfStockItems.includes(item.id);
  return categories
    .map((category: any) => ({
      ...category,
      items: category.items
        .filter(inStock)
        .map((item: any) => (item.items?.length ? { ...item, items: item.items.filter(inStock) } : item))
        .filter((item: any) => !item.items || item.items.length),
    }))
    .filter((category: any) => category.items.length);
};

// A theme colour (the --sf-* variable StorefrontThemeHelper writes), "" on stores without a theme
export const storefrontColor = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(`--sf-${name}`).trim();

// True when the theme's page background is dark, for SDK widgets that only offer a light or dark
// look (Google Pay button, reCAPTCHA). False on stores without a theme.
export const storefrontIsDark = (): boolean => {
  const rgb = getComputedStyle(document.documentElement).getPropertyValue("--sf-bg-rgb").trim();
  if (!rgb) return false;
  const [r, g, b] = rgb.split(",").map((v) => parseInt(v, 10) / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) < 0.2;
};

// Adyen Drop-in card fields render in Adyen's iframes; their text colours go in the card's styles
// option. Undefined on stores without a theme, so Adyen keeps its default look.
export const storefrontAdyenCardStyles = (): any => {
  if (!storefrontColor("ink")) return undefined;
  return {
    base: { color: storefrontColor("ink"), caretColor: storefrontColor("ink") },
    placeholder: { color: storefrontColor("muted") },
    error: { color: storefrontColor("error") || undefined },
  };
};
